import Markdown from 'markdown-to-jsx';
import React, { useState } from 'react';

import Heading from '../Typography/Heading';
import Text from '../Typography/Text';
import {
  codeBlockHeaderStyles,
  codeBlockLanguageStyles,
  codeBlockPreStyles,
  codeBlockStyles,
  getCodeBlockCopyButtonStyles,
  inlineCodeStyles,
  markdownContentStyles,
  tableWrapperStyles,
} from './MarkdownContent.styles';

export type MarkdownContentProps = {
  /**
   * The markdown content to render
   */
  content: string;
  /**
   * Additional CSS class names
   */
  className?: string;
  /**
   * Whether to enable code copy functionality
   * @default true
   */
  enableCodeCopy?: boolean;
  /**
   * Custom React components to be available in markdown
   * @example { MyComponent: MyComponent }
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customComponents?: Record<string, React.ComponentType<any>>;
};

interface CodeBlockProps {
  children: string;
  className?: string;
  enableCopy?: boolean;
}

const CodeBlock: React.FC<CodeBlockProps> = ({
  children,
  className,
  enableCopy = true,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Extract language from className (format: "language-javascript")
  const language = className?.replace('language-', '') || 'text';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <div
      css={codeBlockStyles}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Header with language and copy button */}
      <div css={codeBlockHeaderStyles}>
        <span css={codeBlockLanguageStyles}>{language}</span>

        {enableCopy && (
          <button
            onClick={handleCopy}
            css={getCodeBlockCopyButtonStyles(isHovered, copySuccess)}
          >
            {copySuccess ? 'Copied!' : 'Copy'}
          </button>
        )}
      </div>

      {/* Code content */}
      <pre css={codeBlockPreStyles}>
        <code>{children}</code>
      </pre>
    </div>
  );
};

const InlineCode: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <code css={inlineCodeStyles}>{children}</code>;
};

const TableWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return <div css={tableWrapperStyles}>{children}</div>;
};

const MarkdownContent: React.FC<MarkdownContentProps> = ({
  content,
  className,
  enableCodeCopy = true,
  customComponents = {},
}) => {
  return (
    <div className={className} css={markdownContentStyles}>
      <Markdown
        options={{
          overrides: {
            // Custom components (e.g., TopMatchingFieldNote, TopMatchingReview)
            ...customComponents,
            // Headings - use design system Heading component with spacing
            h1: {
              component: ({ children }: React.HTMLAttributes<HTMLElement>) => (
                <Heading
                  size="2xs"
                  as="h1"
                  fontWeight="bold"
                  css={{
                    '&:not(:first-child)': { marginTop: 'var(--spacing-6)' },
                    '& + *': { marginTop: 'var(--spacing-3)' },
                  }}
                >
                  {children}
                </Heading>
              ),
            },
            h2: {
              component: ({ children }: React.HTMLAttributes<HTMLElement>) => (
                <Heading
                  size="2xs"
                  as="h2"
                  fontWeight="bold"
                  css={{
                    '&:not(:first-child)': { marginTop: 'var(--spacing-6)' },
                    '& + *': { marginTop: 'var(--spacing-3)' },
                  }}
                >
                  {children}
                </Heading>
              ),
            },
            h3: {
              component: ({ children }: React.HTMLAttributes<HTMLElement>) => (
                <Heading
                  size="2xs"
                  as="h3"
                  fontWeight="bold"
                  css={{
                    '&:not(:first-child)': { marginTop: 'var(--spacing-5)' },
                    '& + *': { marginTop: 'var(--spacing-3)' },
                  }}
                >
                  {children}
                </Heading>
              ),
            },
            h4: {
              component: ({ children }: React.HTMLAttributes<HTMLElement>) => (
                <Heading
                  size="2xs"
                  as="h4"
                  fontWeight="bold"
                  css={{
                    '&:not(:first-child)': { marginTop: 'var(--spacing-5)' },
                    '& + *': { marginTop: 'var(--spacing-3)' },
                  }}
                >
                  {children}
                </Heading>
              ),
            },
            h5: {
              component: ({ children }: React.HTMLAttributes<HTMLElement>) => (
                <Heading
                  size="2xs"
                  as="h5"
                  fontWeight="bold"
                  css={{
                    '&:not(:first-child)': { marginTop: 'var(--spacing-5)' },
                    '& + *': { marginTop: 'var(--spacing-3)' },
                  }}
                >
                  {children}
                </Heading>
              ),
            },
            h6: {
              component: ({ children }: React.HTMLAttributes<HTMLElement>) => (
                <Heading
                  size="2xs"
                  as="h6"
                  fontWeight="bold"
                  css={{
                    '&:not(:first-child)': { marginTop: 'var(--spacing-5)' },
                    '& + *': { marginTop: 'var(--spacing-3)' },
                  }}
                >
                  {children}
                </Heading>
              ),
            },
            // Paragraphs - use design system Text component with spacing
            p: {
              component: ({ children }: React.HTMLAttributes<HTMLElement>) => (
                <Text
                  size="md"
                  css={{
                    '&:not(:first-child)': { marginTop: 'var(--spacing-4)' },
                  }}
                >
                  {children}
                </Text>
              ),
            },
            // Override pre tag for code blocks
            pre: {
              component: ({
                children,
              }: React.HTMLAttributes<HTMLPreElement>) => {
                // Extract the code element and its props
                const codeElement = React.Children.only(
                  children as React.ReactElement
                );
                const codeProps = codeElement?.props || {};
                const codeClassName = codeProps.className || '';
                const codeChildren = codeProps.children || '';

                // markdown-to-jsx uses 'lang-' prefix (e.g., 'lang-javascript')
                const match = /lang-(\w+)/.exec(codeClassName);
                const language = match ? match[1] : 'text';

                return (
                  <CodeBlock
                    className={`language-${language}`}
                    enableCopy={enableCodeCopy}
                  >
                    {String(codeChildren).replace(/\n$/, '')}
                  </CodeBlock>
                );
              },
            },
            // Override code tag for inline code only
            code: {
              component: ({ children }: React.HTMLAttributes<HTMLElement>) => {
                return <InlineCode>{children}</InlineCode>;
              },
            },
            table: {
              component: ({
                children,
                ...props
              }: React.HTMLAttributes<HTMLTableElement>) => (
                <TableWrapper>
                  <table {...props}>{children}</table>
                </TableWrapper>
              ),
            },
          },
        }}
      >
        {content}
      </Markdown>
    </div>
  );
};

export default MarkdownContent;
